import type {
  TRIOnsiteReleaseQuantityDataType,
  TRIPOTWWasteQuantityDataType,
  TRITransferLocationDataType,
} from '../../types/form-r.ts';
import type { Warning } from '../../types/tri.ts';
import type { CasOrCategory } from '../../types/db.ts';
import type { OffSiteLocationID, PotwID, QuestionnaireAnswers } from '../../types/questionnaires.ts';
import type { FormRTransferLocationRegistrar, ReleasesByChemicalAndFormRLine } from './form-r-utils.ts';
import { formRRegisterTransferLocation } from './form-r-utils.ts';
import { formRSection5OnSiteReleases } from './form-r-section-5-on-site-releases.ts';
import { formRSection61PotwReleases } from './form-r-section-6.1-potw-releases.ts';
import { formRSection62OffSiteLocationReleases } from './form-r-section-6.2-off-site-location-releases.ts';
import { formRSection8Sums, type Section8Quantities } from './form-r-section-8-sums.ts';

export function formRCurrentYearChemicalReleaseDataBuild({
  casOrCategory,
  chemicalName,
  releasesByChemical,
  questionnaireAnswers,
  warnings,
  transferLocationsWithMissingData,
}: {
  casOrCategory: CasOrCategory;
  chemicalName: string;
  releasesByChemical: ReleasesByChemicalAndFormRLine;
  questionnaireAnswers: QuestionnaireAnswers;
  warnings: Warning[];
  transferLocationsWithMissingData: Set<OffSiteLocationID | PotwID>;
}): {
  OnsiteReleaseQuantity: TRIOnsiteReleaseQuantityDataType[];
  POTWWasteQuantity: TRIPOTWWasteQuantityDataType[];
  TransferLocation: TRITransferLocationDataType[];
  section8CurrentYearQuantities: Section8Quantities;
} {
  const { OnsiteReleaseQuantity, OnsiteUICDisposalQuantity, OnsiteOtherDisposalQuantity } = formRSection5OnSiteReleases(
    {
      chemicalName,
      casOrCategory,
      releasesByChemical,
      wasteTransfers: questionnaireAnswers.wasteTransfers,
      streams: questionnaireAnswers.streams,
      warnings,
    },
  );

  const TransferLocation: TRITransferLocationDataType[] = [];
  const transferLocationsByID = new Map<PotwID | OffSiteLocationID, TRITransferLocationDataType>();
  const registerTransferLocation: FormRTransferLocationRegistrar = (location) =>
    formRRegisterTransferLocation(
      TransferLocation,
      transferLocationsByID,
      location,
      warnings,
      transferLocationsWithMissingData,
    );

  const POTWWasteQuantity = formRSection61PotwReleases({
    chemicalName,
    casOrCategory,
    sectionSixOneReleases: releasesByChemical.get(`${casOrCategory}-6.1`),
    wasteTransfers: questionnaireAnswers.wasteTransfers,
    potws: questionnaireAnswers.potws,
    potwDistributionsKnown: questionnaireAnswers.potwDistributionsKnown,
    registerTransferLocation,
    warnings,
  });

  formRSection62OffSiteLocationReleases({
    chemicalName,
    casOrCategory,
    sectionSixTwoReleases: releasesByChemical.get(`${casOrCategory}-6.2`),
    wasteTransfers: questionnaireAnswers.wasteTransfers,
    offSiteLocations: questionnaireAnswers.offSiteLocations,
    registerTransferLocation,
    warnings,
  });

  return {
    OnsiteReleaseQuantity,
    POTWWasteQuantity,
    TransferLocation,
    section8CurrentYearQuantities: {
      ...formRSection8Sums(casOrCategory, releasesByChemical, POTWWasteQuantity, TransferLocation, warnings),
      OnsiteUICDisposalQuantity,
      OnsiteOtherDisposalQuantity,
    },
  };
}
